<div class="content-wrapper">
  <app-funnel-trust [quote]="funnelQuote" />

  <div class="what-i-teach-intro">
    <div class="what-i-teach-intro-text">
      <p>
        I have been playing and teaching bass guitar professionally since 1985.
        Over four decades of gigs, studio work, and lessons, I have covered the
        major styles, genres, and skills that make up a working bass player's
        toolbox. I have played and taught nearly every style that comes up on a
        gig. If you don't see your style listed below, reach out. Chances are
        I've taught it.
      </p>
    </div>

    <figure class="what-i-teach-photo">
      <img
        ngSrc="/images/funnel/lesson-what-i-teach.webp"
        width="960"
        height="720"
        alt="Russ playing bass with sheet music and a bass amp visible behind him"
        sizes="(max-width: 48rem) 100vw, 22rem"
      />
      <figcaption>Russ working through material.</figcaption>
    </figure>
  </div>

  <section>
    <h2 id="styles">Styles</h2>
    <p>
      My teaching covers the styles I have played on professional gigs and in
      studio sessions:
      <strong>rock</strong>, <strong>pop</strong>, <strong>R&amp;B</strong>,
      <strong>funk</strong>, <strong>blues</strong>,
      <strong>jazz</strong>
      (including
      <a [routerLink]="'/blog/walking-bass-lines-2'">walking bass</a>,
      <a [routerLink]="'/blog/bebop-arpeggios'">bebop</a>, swing, and Latin
      jazz), <strong>gospel</strong>, <strong>country</strong>,
      <strong>reggae</strong>, and
      <strong>Latin</strong>
      (salsa, samba, bossa nova). When working with different styles the focus
      should be on the groove and feel. With the use of play-along audio tracks,
      we will work with the different elements that make each style feel and
      sound the way it does. With a conceptual understanding of what groove is,
      the elements that need to be worked on become obvious. Lessons are built
      around the styles and songs that matter to you.
    </p>
  </section>

  <!-- id="fundamentals" is load-bearing: nearly every blog post's closing CTA
       deep-links /bass-lessons/what-i-teach#fundamentals, and the fundamentals
       run starts here. -->
  <!-- NOTE MAPPING is the frame Russ endorsed on the 2026-08-11 call (roadmap
       #107, outcome 12): the job is organizing the note choices available over
       a chord, and arpeggios and scales are the two things that feed it. This
       section used to be headed "Arpeggios & Scales" with the two blended into
       one paragraph; Jeremy restructured it 2026-08-12 so note mapping is the
       subject and the two are its inputs. Russ's own prose below is verbatim
       and untouched. The excerpt IMAGES that briefly lived here moved to
       /blog/scales-on-bass-the-number-system-approach, which is where they
       belong (Jeremy, 2026-08-12). -->
  <section id="fundamentals">
    <h2 id="note-mapping">Note Mapping</h2>
    <p>
      Over any chord there is a set of notes available to you. Note mapping is
      how I organize those choices: where each note sits under your hand, and
      what it does against the chord you are playing over. Arpeggios and scales
      are the two things that feed it.
    </p>
    <p>
      I believe that the notes of the arpeggios and scales are the alphabet
      and/or the vocabulary for the language of music. If someone asks me what
      is going on in my head as I improvise, I would answer, "Well, what goes on
      in your head when you talk to somebody? Do you think about each word as
      you say it? Do you think about whether each word is a noun, a verb, or an
      adjective?" No, you simply use the words to communicate. It is the exact
      same thing with the notes of music. My definition of music is that it is
      the language of emotion or the communication of feelings through the use
      of sounds. The player feels; then plays. The listener hears; then feels.
      The arpeggio/scales part of my program is based on this concept. What
      needs to be done with the notes is to get them to be just like the words
      you use when you talk to somebody. My scales program of study gets this
      done in a quick, efficient way that really works. See
      <a [routerLink]="'/blog/why-triads-matter-for-bass-players'">
        why triads matter more than scales
      </a>
      and
      <a [routerLink]="'/blog/adding-harmony-to-a-bass-line-2'">
        adding harmony to a bass line
      </a>
      for the harmony work specifically.
    </p>
    <!-- Pull-quote from the prose above — verbatim (Russ on the language of
         music). -->
    <figure groovePullquote variant="card" size="md" class="wit-pull">
      <blockquote>
        My definition of music is that it is the language of emotion or the
        communication of feelings through the use of sounds. The player feels;
        then plays. The listener hears; then feels.
      </blockquote>
      <figcaption>Russ, on the language of music</figcaption>
    </figure>

    <h3 id="arpeggios">Arpeggios</h3>
    <p>
      The arpeggio is the chord itself, one note at a time: the root, the third,
      the fifth, and the seventh. Those are the notes that spell what the band
      is playing, so they are the strongest choices you have over that chord,
      and they are where I start.
    </p>

    <h3 id="scales">Scales</h3>
    <p>
      The scale is the wider set of notes that works over the same chord, the
      arpeggio included. Learn both by their function rather than by letter and
      you are naming what a note does instead of memorizing where it sits, which
      is what makes the whole thing transpose to any key.
      <a [routerLink]="'/blog/scales-on-bass-the-number-system-approach'">
        The number system approach
      </a>
      lays that out on the neck, with pages from the material I hand my
      students.
    </p>
  </section>

  <section>
    <h2 id="ear-development">Ear Development</h2>
    <p>
      I asked a jazz piano player I worked with years ago what do you think I
      should play here in this part of a song? He looked up at me with an
      annoyed expression on his face and said what are you asking me for, if you
      are really listening to the music it will tell you what to do every time,
      don't ask me, ask the music. That's when I really started listening. Yes,
      it is important to be able to identify intervals and chords, but the real
      magic lives in being able to play what you hear. I can help you to never
      underestimate the power of a well developed ear. The ear development part
      of my teaching is based mostly on doing different types of transcription.
    </p>
    <p>
      <a [routerLink]="'/blog/ear-training-can-you-hear-me-now-2'">
        Ear training exercises
      </a>
      and
      <a
        [routerLink]="'/blog/ear-training-a-way-to-work-on-chromatic-intervals'"
      >
        ear training using chromatic intervals
      </a>
      give you a sense of what that work looks like.
    </p>
    <!-- Pull-quote from the prose above — the jazz pianist's words, verbatim
         from the story Russ tells. -->
    <figure groovePullquote variant="card" size="md" class="wit-pull">
      <blockquote>
        What are you asking me for? If you are really listening to the music it
        will tell you what to do every time. Don't ask me, ask the music.
      </blockquote>
      <figcaption>Jazz piano player Russ worked with, on listening</figcaption>
    </figure>
  </section>

  <section>
    <h2 id="reading">Reading</h2>
    <p>
      Music notation is a way to communicate on paper what you want someone else
      to play. If you are considering playing the bass professionally, then
      having this skill is one of the keys to a successful career. My reading
      program is divided into 3 main sections: first, knowing all of the note
      names on the bass neck; second, knowing the written range of the bass,
      being able to identify the note on the page and then its location on the
      bass neck; and third, learning how to count and play the rhythmic notation
      on the page. After you have a working knowledge of these basic reading
      skills, we move on to professional bass charts. I have collected several
      hundred of these charts throughout my professional career including charts
      from studio recording sessions. These charts will give you real-life
      experience and preparation for professional reading situations.
    </p>
    <!-- Internal links added 2026-08-12: this section and Ear Development were
         the only two on the page with none, while Styles, Note Mapping, and
         Technique all had them. The three posts map onto the three parts of
         the reading program Russ describes just above, in his order. -->
    <p>
      There is a post for each part of that:
      <a [routerLink]="'/blog/prep-steps-for-working-on-your-note-reading'">
        note reading preparation
      </a>
      for the neck and the page,
      <a [routerLink]="'/blog/reading-music-a-good-thing-to-do-2'">
        reading basics
      </a>
      for the notation itself, and
      <a [routerLink]="'/blog/rhythm-reading-basics'">rhythm reading basics</a>
      for the counting.
    </p>
  </section>

  <section>
    <h2 id="technique-and-application">Technique &amp; Application</h2>
    <p>
      Beyond the foundational work above, lessons also cover
      <strong>right- and left-hand technique</strong>,
      <strong>bass line construction</strong>, <strong>soloing</strong>, and
      <strong>groove and time-feel</strong>. A post on each to start with:
      <a [routerLink]="'/blog/left-hand-form'">left hand form</a> and
      <a [routerLink]="'/blog/right-hand-form-choices-for-your-bass-playing-2'">
        right hand form choices
      </a>
      for the hands,
      <a [routerLink]="'/blog/anatomy-of-a-bass-line-2'">
        anatomy of a bass line
      </a>
      for construction,
      <a [routerLink]="'/blog/how-to-stay-on-point-with-your-solos'">
        staying on point with your solos
      </a>
      for soloing, and
      <a [routerLink]="'/blog/the-groove-denominator'"
        >the groove denominator</a
      >
      for groove. Each is explored further across the
      <a [routerLink]="'/blog'">bass guitar lessons blog</a>.
    </p>
  </section>

  <section id="example-lessons" class="example-lessons">
    <h2>See how I teach</h2>
    <p>
      I picked four videos that map to how I teach: the language of music,
      scales as a foundation, building real bass lines, and what to actually
      practice.
    </p>
    <aside class="playlist-cta">
      <p class="playlist-cta__lead">
        These four are a starting point. There are 130+ more free lessons on my
        full YouTube playlist.
      </p>
      <a grooveAction [href]="playlistUrl" target="_blank" rel="noopener">
        Watch all 130+ lessons on YouTube
        <span class="visually-hidden">(opens in new tab)</span>
      </a>
    </aside>

    <section>
      <h3>Music is a language</h3>
      <p>
        Music is a language. This is where I start when a student first asks me
        what to practice.
      </p>
      <app-youtube-section
        resource="video:fXeLnNk3y4U"
        placeholderWebpImage="false"
      />
    </section>

    <section>
      <h3>Defining a scale</h3>
      <p>
        Scales come up in every conversation about practice, and this video is
        three minutes on the way I look at them.
      </p>
      <app-youtube-section
        resource="video:82464BLwDws"
        placeholderWebpImage="false"
      />
    </section>

    <section>
      <h3>What notes go where, and why</h3>
      <p>
        Constructing a bass line is more than picking the right notes, and when
        a student wants to know why a line works I walk through the reasons the
        way I do here.
      </p>
      <app-youtube-section
        resource="video:p_B16KQorow"
        placeholderWebpImage="false"
      />
    </section>

    <section>
      <h3>Practice music, not exercises</h3>
      <p>
        I say this to every student in their first lesson, and I keep coming
        back to it as they go.
      </p>
      <app-youtube-section
        resource="video:2HmBfhvNw8I"
        placeholderWebpImage="false"
      />
    </section>
  </section>
</div>
